import { useMutation, useQuery } from "@apollo/client/react"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import { useViewer } from "~/auth/use-viewer"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"
import { ScreenLoading } from "~/ui/screen-loading"
import { makeColumns } from "./columns"
import { DataTable } from "./data-table"

export const usersQuery = gql(/* GraphQL */ `
  query Users($first: Int = 20, $after: String) {
    internalAdminUsers(first: $first, after: $after) {
      edges {
        node {
          id
          fullName
          email
          createdAt
          userStatus
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`)

const suspendMutation = gql(/* GraphQL */ `
  mutation InternalAdminUserSuspend($input: InternalAdminUserSuspendInput!) {
    internalAdminUserSuspend(input: $input) {
      user {
        id
        userStatus
      }
    }
  }
`)

const unsuspendMutation = gql(/* GraphQL */ `
  mutation InternalAdminUserUnsuspend($input: InternalAdminUserUnsuspendInput!) {
    internalAdminUserUnsuspend(input: $input) {
      user {
        id
        userStatus
      }
    }
  }
`)

export const InternalAdminDashboardScreen = () => {
  const { viewer } = useViewer()
  const { data, fetchMore, error, loading } = useQuery(usersQuery)
  const [suspend, { loading: suspending }] = useMutation(suspendMutation, { onError: () => null })
  const [unsuspend, { loading: unsuspending }] = useMutation(unsuspendMutation, {
    onError: () => null,
  })

  const users = data?.internalAdminUsers.edges.map((edge) => edge.node) ?? []
  const pageInfo = data?.internalAdminUsers.pageInfo

  const columns = makeColumns({
    viewerId: viewer?.id,
    busy: suspending || unsuspending,
    onSuspend: async (id) => {
      const r = await suspend({ variables: { input: { id } } })
      if (r.data) toast.success("User suspended")
    },
    onUnsuspend: async (id) => {
      const r = await unsuspend({ variables: { input: { id } } })
      if (r.data) toast.success("User unsuspended")
    },
  })

  return (
    <TablePageLayout>
      <h1 className="text-2xl">Users</h1>
      <hr className="my-4 border-0 border-b" />

      {!data && loading ? (
        <ScreenLoading />
      ) : error ? (
        <GraphqlError error={error} />
      ) : (
        <>
          <DataTable columns={columns} data={users} />
          {pageInfo && pageInfo.hasNextPage && (
            <>
              <hr className="my-4 border-0 border-b" />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="disabled:bg-gray-100"
                  onClick={() => {
                    fetchMore({
                      variables: {
                        after: pageInfo.endCursor,
                      },
                    })
                  }}
                  disabled={!pageInfo.hasNextPage}
                >
                  Load more
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </TablePageLayout>
  )
}
